import { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, Chip, Divider, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import SearchField from '../common/SearchField';
import { useGetGstRegisterPageMutation } from '../../api/reports/ReportApi';
import { GstRegisterRow } from '../../types/reports';
import { exportToExcel } from '../../utils/exportExcel';
import { longDate, invoiceDate } from '../../utils/dates';

const money = (value?: number | null) => Number(value || 0).toFixed(2);
const centred = { flex: 1, align: 'center', headerAlign: 'center' } as const;

/** Local date as yyyy-MM-dd. toISOString() would shift to UTC and lose a day. */
const iso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const monthStart = () => {
  const now = new Date();
  return iso(new Date(now.getFullYear(), now.getMonth(), 1));
};

const today = () => iso(new Date());

/**
 * The GST register: every bill in the period, numbered in order.
 *
 * <p>Grouped by tax rate this answered "how much 18% tax did we collect", which is
 * what filing a return needs. A shop checking its own books wants the other shape -
 * the bills themselves, in order, each with its value, its rate and the tax on it -
 * so that is what this shows, with the period's totals in the footer.
 *
 * <p>The row number comes from the server, so it keeps counting across pages and into
 * the Excel export rather than restarting at 1 on every page.
 */
const GstReport = () => {
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [searchValue, setSearchValue] = useState('');

  const [rows, setRows] = useState<GstRegisterRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [totals, setTotals] = useState({ billAmount: 0, gstAmount: 0, cgst: 0, sgst: 0, igst: 0, netAmount: 0 });
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getGstRegisterPage] = useGetGstRegisterPageMutation();

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getGstRegisterPage({
          searchValue,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
        setTotals({
          billAmount: Number(response?.billAmount || 0),
          gstAmount: Number(response?.gstAmount || 0),
          cgst: Number(response?.cgstAmount || 0),
          sgst: Number(response?.sgstAmount || 0),
          igst: Number(response?.igstAmount || 0),
          netAmount: Number(response?.netAmount || 0),
        });
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, fromDate, toDate, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, fromDate, toDate]);

  const columns: GridColDef[] = [
    { field: 'serialNo', headerName: 'S.No', ...centred, minWidth: 70, maxWidth: 90, sortable: false },
    { field: 'billNo', headerName: 'Bill Number', ...centred, minWidth: 140, align: 'left', headerAlign: 'left' },
    {
      field: 'billDate',
      headerName: 'Bill Date',
      ...centred,
      minWidth: 120,
      renderCell: (row) => invoiceDate(row.value as string),
    },
    {
      field: 'billAmount',
      headerName: 'Bill Amount',
      ...centred,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'gstPercentage',
      headerName: 'GST %',
      ...centred,
      minWidth: 100,
      // A bill can carry more than one rate. Both are shown rather than splitting the
      // bill across two lines, which would break the numbering.
      renderCell: (row) => <Chip size="small" variant="outlined" label={String(row.value || '-')} />,
    },
    {
      field: 'placeOfSupply',
      headerName: 'Place of Supply',
      ...centred,
      minWidth: 130,
      renderCell: (row) => String(row.value || '-'),
    },
    {
      field: 'gstAmount',
      headerName: 'GST Value',
      ...centred,
      minWidth: 120,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'netAmount',
      headerName: 'Net Amount (incl. GST)',
      ...centred,
      minWidth: 170,
      align: 'right',
      headerAlign: 'right',
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={700}>
          {money(row.value as number)}
        </Typography>
      ),
    },
  ];

  /** The export is the page on screen - the same rows the shop is looking at. */
  const download = () =>
    exportToExcel(
      rows.map((row) => ({
        'S.No': row.serialNo,
        'Bill Number': row.billNo,
        'Bill Date': row.billDate,
        'Bill Amount': row.billAmount,
        'GST %': row.gstPercentage,
        'Place of Supply': row.placeOfSupply,
        CGST: row.cgstAmount,
        SGST: row.sgstAmount,
        IGST: row.igstAmount,
        'GST Value': row.gstAmount,
        'Net Amount': row.netAmount,
      })),
      `gst-register-${fromDate}-to-${toDate}`,
      'GST',
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder="Any words: bill number, customer, mobile..." width={280} />
        <TextField label="From" size="small" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
        <TextField label="To" size="small" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(event) => setToDate(event.target.value)} />
        <Chip variant="outlined" label={`${rowCount} bill(s)`} />
        <Box flexGrow={1} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      <DataGrid
        columns={columns}
        rows={rows}
        getRowId={(row) => row.serialNo}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        sx={{ height: '60vh' }}
      />

      {/* The totals cover the whole period, not the page on screen. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Card sx={{ p: 2, minWidth: 340, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary.main">
            TOTALS FOR THIS PERIOD
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Bill amount, before tax</Typography>
            <Typography variant="body2">{money(totals.billAmount)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">CGST</Typography>
            <Typography variant="body2">{money(totals.cgst)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2">SGST</Typography>
            <Typography variant="body2">{money(totals.sgst)}</Typography>
          </Stack>
          {/* Only when there were inter-state sales in the period. */}
          {totals.igst > 0 && (
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">IGST</Typography>
              <Typography variant="body2">{money(totals.igst)}</Typography>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              GST value
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {money(totals.gstAmount)}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={700}>
              Net amount, including GST
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {money(totals.netAmount)}
            </Typography>
          </Stack>
        </Card>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        One line per bill, numbered in date order. Cancelled bills are left out, because a cancelled sale collected no tax.
        CGST and SGST are half the GST value each, for a sale inside the state.
      </Typography>
    </Box>
  );
};

export default GstReport;
